import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny in-memory cache so SPA navigation doesn't refetch overrides every render.
const overrideCache = new Map();

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Escape HTML for safe innerHTML injection
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Convert replacement text to HTML: \n → <br>, other characters HTML-escaped.
// Preserves intentional line-breaks Speicher types in the "Proposed copy" field.
function replacementToHtml(text) {
  return escapeHtml(text).replace(/\r?\n/g, "<br>");
}

// Build a whitespace-tolerant regex pattern: any run of whitespace in `currentText`
// matches any run of whitespace in the DOM (spaces, tabs, newlines, NBSP, etc.).
// Also accepts em-dash <-> en-dash <-> hyphen interchangeably so Speicher's curly-quote
// editor input doesn't fail to match against straight characters in the HTML.
export function buildPattern(currentText, { caseInsensitive = false } = {}) {
  const trimmed = currentText.trim();
  if (!trimmed) return null;
  // Split on any whitespace; escape each chunk; then loosen specific punctuation classes.
  const escapedChunks = trimmed.split(/\s+/).map((chunk) => {
    let esc = escapeRegex(chunk);
    // Treat em-dash, en-dash, hyphen-minus all as interchangeable
    esc = esc.replace(/[\u2014\u2013\-]/g, "[\\u2014\\u2013\\-]");
    // Treat smart quotes and straight quotes interchangeably
    esc = esc.replace(/['\u2018\u2019]/g, "['\\u2018\\u2019]");
    esc = esc.replace(/["\u201C\u201D]/g, "[\"\\u201C\\u201D]");
    return esc;
  });
  const flags = caseInsensitive ? "gi" : "g";
  return new RegExp(escapedChunks.join("\\s+"), flags);
}

// Get the visible text of an element, but treat <br> tags as a space so that the
// resulting string matches what a human would read on screen. This is critical because
// browser's native .textContent treats <br> as zero characters — meaning "foo<br>bar"
// reads as "foobar" via textContent but renders as "foo \n bar" to the human eye.
export function visibleText(el) {
  let out = "";
  for (const c of el.childNodes) {
    if (c.nodeType === Node.TEXT_NODE) {
      out += c.nodeValue || "";
    } else if (c.nodeType === Node.ELEMENT_NODE) {
      if (c.tagName === "BR") {
        out += " ";
      } else {
        out += visibleText(c);
      }
    }
  }
  return out;
}

// Allowed inline HTML tags that an editable text element can contain. Anything more
// complex (nested DIVs, IMG, anchors with attributes, components, etc.) we leave alone
// to avoid clobbering layout.
const SIMPLE_INLINE_TAGS = new Set(["BR", "B", "STRONG", "I", "EM", "U", "SPAN", "A"]);

function isSimpleTextElement(el) {
  // Element only contains text nodes + simple inline tags (no nested complex structure).
  if (!el || !el.childNodes) return false;
  for (const c of el.childNodes) {
    if (c.nodeType === Node.TEXT_NODE) continue;
    if (c.nodeType === Node.ELEMENT_NODE && SIMPLE_INLINE_TAGS.has(c.tagName)) {
      // Inline tag — must itself be simple (no nested complex)
      if (!isSimpleTextElement(c)) return false;
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Walks the live DOM and replaces every occurrence of `currentText` with `replacementText`.
 * Handles three increasingly difficult cases:
 *   1. Text fully inside a single text node → swap the text node value
 *   2. Text split across multiple text nodes inside the same element (e.g. broken by <br>
 *      or wrapped <span> for styling) → check the element's textContent and replace innerHTML
 *   3. Text spanning multiple elements → not supported; logs a warning
 * Whitespace-tolerant; dash-tolerant (em/en/hyphen interchangeable); smart-quote tolerant.
 * Skips <script>, <style>, <input>, <textarea>, <noscript>.
 * Returns count of replacements made.
 */
function replaceInDom(currentText, replacementText) {
  if (!currentText || currentText === replacementText) return 0;
  const pattern = buildPattern(currentText);
  if (!pattern) return 0;
  // Guard: if the replacement itself contains the source pattern, applying this
  // override is inherently recursive (every subsequent pass would keep re-matching
  // the source inside the replacement, appending it again — visible as a runaway
  // "...Mumbai, India in Mumbai, India in Mumbai..." growth). Only allow the
  // replacement to fire if the replacement text does NOT still match the pattern.
  pattern.lastIndex = 0;
  if (pattern.test(replacementText)) {
    return 0;
  }
  pattern.lastIndex = 0;
  const skipParents = new Set(["SCRIPT", "STYLE", "INPUT", "TEXTAREA", "NOSCRIPT", "META", "TITLE"]);
  let count = 0;

  // PASS 1 — Single text-node fast path
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (skipParents.has(node.parentElement?.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);
  textNodes.forEach((node) => {
    const val = node.nodeValue;
    if (!val) return;
    pattern.lastIndex = 0;
    if (!pattern.test(val)) return;
    pattern.lastIndex = 0;
    const newVal = val.replace(pattern, replacementText);
    if (newVal !== val) {
      node.nodeValue = newVal;
      if (node.parentElement) node.parentElement.dataset.speicherEdited = "1";
      count += 1;
    }
  });
  if (count > 0) return count;

  // PASS 2 — Element-level replacement for text split across text nodes / <br> / inline tags
  // Walk visible text-bearing element types and check their combined visible-text content.
  const candidateSelector = "h1,h2,h3,h4,h5,h6,p,li,span,div,a,button,strong,em,blockquote,dd,dt,td,th,figcaption,label";
  const elements = document.querySelectorAll(candidateSelector);
  for (const el of elements) {
    if (skipParents.has(el.tagName)) continue;
    if (!el.childNodes || el.childNodes.length === 0) continue;
    if (el.dataset && el.dataset.speicherOverride) continue;
    if (!isSimpleTextElement(el)) continue;
    const txt = visibleText(el);
    pattern.lastIndex = 0;
    if (!pattern.test(txt)) continue;
    pattern.lastIndex = 0;
    const newTxt = txt.replace(pattern, replacementText);
    if (newTxt === txt) continue;

    // Preserve any <a>, <strong>, <b>, <em>, <i>, <u> children whose visible text
    // still appears in the new content. This keeps Speicher's internal SEO links
    // (e.g. "Bhiwandi factory" → /manufacturing) intact after copy edits.
    const preserveTags = ["A", "STRONG", "B", "EM", "I", "U"];
    const preservers = [];
    for (const tagName of preserveTags) {
      el.querySelectorAll(tagName.toLowerCase()).forEach((child) => {
        const childText = (child.textContent || "").trim();
        if (childText) preservers.push({ text: childText, outerHTML: child.outerHTML });
      });
    }
    let newHtml = replacementToHtml(newTxt);
    preservers.forEach(({ text, outerHTML }) => {
      const escapedText = escapeHtml(text);
      const idx = newHtml.indexOf(escapedText);
      if (idx !== -1) {
        newHtml = newHtml.slice(0, idx) + outerHTML + newHtml.slice(idx + escapedText.length);
      }
    });
    el.innerHTML = newHtml;
    if (el.dataset) el.dataset.speicherOverride = "1";
    count += 1;
  }
  return count;
}

/**
 * usePageContentOverrides — fetches admin-approved content overrides for the given path
 * and applies them in-place to the rendered DOM. Use on every public page where
 * the SEO agency might propose copy changes.
 *
 * Usage:
 *   usePageContentOverrides("/about");
 *
 * Idempotent across SPA navigation. Cached for the session.
 */
export default function usePageContentOverrides(path) {
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    let observer = null;

    const applyOverrides = (overrides) => {
      if (!overrides || overrides.length === 0) return;
      // Multiple micro-attempts because React can re-render content after our first pass.
      let attempts = 0;
      const tryApply = () => {
        if (cancelled) return;
        overrides.forEach((o) => replaceInDom(o.current_text, o.replacement_text));
        attempts += 1;
        if (attempts < 4) setTimeout(tryApply, 400);
      };
      tryApply();
      // MutationObserver: re-apply whenever React updates the DOM (e.g., async data load).
      // CRITICAL: throttle via rAF + auto-disconnect after settling window, otherwise
      // framer-motion animations and continuous React re-renders trigger the observer
      // hundreds of times per second, each scanning the entire document.body — which
      // pins the CPU and hangs the browser tab. We only need the observer active long
      // enough for async data (product galleries, JSON-LD, etc.) to hydrate.
      let scheduled = false;
      let mutationCount = 0;
      const MAX_MUTATIONS = 200; // hard ceiling — bail out early if runaway
      observer = new MutationObserver(() => {
        mutationCount += 1;
        if (mutationCount > MAX_MUTATIONS) {
          if (observer) { observer.disconnect(); observer = null; }
          return;
        }
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          if (cancelled) return;
          overrides.forEach((o) => replaceInDom(o.current_text, o.replacement_text));
        });
      });
      observer.observe(document.body, { childList: true, subtree: true, characterData: false });
      // Disconnect after 8s: page is fully hydrated by then, further observation is wasted CPU.
      setTimeout(() => {
        if (observer) { observer.disconnect(); observer = null; }
      }, 8000);
    };

    if (overrideCache.has(path)) {
      applyOverrides(overrideCache.get(path));
    } else {
      axios
        .get(`${API}/seo-public/content-overrides`, { params: { path } })
        .then((r) => {
          if (cancelled) return;
          const data = r.data || [];
          overrideCache.set(path, data);
          applyOverrides(data);
        })
        .catch(() => {
          overrideCache.set(path, []);
        });
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
    };
  }, [path]);
}
